/**
 * Time Role - Convert Pillar
 *
 * Conversão entre unidades de tempo usando hub-and-spoke (via second).
 *
 * @example
 * import { convertTime, toBaseTime, fromBaseTime } from '@attrx/role-morphic/time/convert';
 *
 * convertTime('hour', 'minute', 1);  // 60
 * toBaseTime('minute', 5);           // 300 (seconds)
 * fromBaseTime('hour', 3600);        // 1
 */

import { TIME_UNITS, type TimeUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unidade base para conversões (hub) */
export const TIME_BASE: TimeUnit = "second";

// =============================================================================
// CORE CONVERSION
// =============================================================================

/**
 * Converte valor para a unidade base (second)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em seconds
 *
 * @example
 * toBaseTime('minute', 5);  // 300
 * toBaseTime('hour', 1);    // 3600
 */
export function toBaseTime(variant: TimeUnit, value: number): number {
  const config = TIME_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown time unit: ${variant}`);
  }
  return value * (config.factor ?? 1);
}

/**
 * Converte valor da unidade base (second) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em seconds
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseTime('minute', 300);  // 5
 * fromBaseTime('hour', 3600);   // 1
 */
export function fromBaseTime(variant: TimeUnit, baseValue: number): number {
  const config = TIME_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown time unit: ${variant}`);
  }
  return baseValue / (config.factor ?? 1);
}

/**
 * Converte entre duas unidades de tempo
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertTime('hour', 'minute', 1);  // 60
 * convertTime('day', 'hour', 1);     // 24
 */
export function convertTime(
  from: TimeUnit,
  to: TimeUnit,
  value: number,
): number {
  if (from === to) {
    return value;
  }
  const baseValue = toBaseTime(from, value);
  return fromBaseTime(to, baseValue);
}

/**
 * Versão safe de convertTime que retorna Result
 *
 * @example
 * tryConvertTime('hour', 'minute', 1);   // { ok: true, value: 60 }
 * tryConvertTime('invalid', 'minute', 1); // { ok: false, error: '...' }
 */
export function tryConvertTime(
  from: TimeUnit,
  to: TimeUnit,
  value: number,
): Result<number> {
  try {
    return { ok: true, value: convertTime(from, to, value) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se uma variante existe
 *
 * @example
 * hasTimeVariant('second');   // true
 * hasTimeVariant('invalid');  // false
 */
export function hasTimeVariant(variant: string): variant is TimeUnit {
  return variant in TIME_UNITS;
}

/**
 * Retorna lista de todas as variantes disponíveis
 *
 * @example
 * getTimeVariants();  // ['nanosecond', 'microsecond', 'millisecond', ...]
 */
export function getTimeVariants(): TimeUnit[] {
  return Object.keys(TIME_UNITS) as TimeUnit[];
}
